import * as React from 'react';

import { translate } from '~l10n/client';
import { useAtom } from 'jotai';

import { ConfirmTransform, Dialog } from '~shared/components/Dialog';
import { toast } from '~shared/components/Toast';
import { useRegenerateWorkspaceCode } from '~teacher/hook/workspaces/code';

import { WorkspaceRole } from '../../../../../api/workspaces/getWorkspaceMembers';
import { workspaceInviteDialogAtom } from '../atoms/atoms';

export interface ResetInviteConfirmationProps {
  workspaceId: string;
  isOpen: boolean;
  handleClose: () => void;
}

export function ResetInviteConfirmation({
  workspaceId,
  isOpen,
  handleClose,
}: ResetInviteConfirmationProps) {
  const [{ currentMemberLinkType }, dispatch] = useAtom(
    workspaceInviteDialogAtom
  );
  const onClose = () => dispatch({ type: 'Close' });

  const { isLoading, resetInviteCode } = useResetInviteCode({
    workspaceId: workspaceId,
    workspaceRole: currentMemberLinkType,
    handleOverlayClose: onClose,
  });

  return (
    <Dialog
      onClose={handleClose}
      padding={'confirmation'}
      id="invite-workspace-dialog"
      open={isOpen}
      title={''}
      closeLabel={translate('Cancel')}
    >
      <ConfirmTransform
        isLoading={isLoading}
        title={translate('Reset Invite')}
        confirm={translate('Reset Invite')}
        onClose={onClose}
        action={resetInviteCode}
        description={translate(
          'People will no longer be able to use the current invite.'
        )}
      />
    </Dialog>
  );
}

interface UseResetInviteCodeParams {
  workspaceId: string;
  workspaceRole: WorkspaceRole;
  handleOverlayClose: () => void;
}

function useResetInviteCode({
  workspaceId,
  workspaceRole,
  handleOverlayClose,
}: UseResetInviteCodeParams) {
  const [isLoading, setIsLoading] = React.useState(false);
  const resetCode = useRegenerateWorkspaceCode();

  const resetInviteCode = () => {
    setIsLoading(true);
    resetCode.mutate(
      {
        workspaceId: workspaceId,
        workspaceRole: workspaceRole,
      },
      {
        onSuccess: () => {
          toast(translate('Invite reset'));
          handleOverlayClose();
        },
        onSettled: () => {
          setIsLoading(false);
        },
        onError: () => {
          toast.critical(translate('An error has occurred'));
        },
      }
    );
  };
  return { isLoading, resetInviteCode };
}
